/* ============================================================
   kula-custom.js — Kula Modern custom behavior (Horizon 4.1.x)
   UPDATE-SAFE asset. Loaded once via layout/theme.liquid:
     <script src="{{ 'kula-custom.js' | asset_url }}" defer></script>
   Additive + guarded: enhances, never re-binds native components,
   and no-ops safely if its targets aren't present.
   ============================================================ */
(function () {
  'use strict';
  var ESPRESSO = '#35332d';

  /* Collect matches across the light DOM AND any OPEN shadow roots.
     4.1.1 wraps the add-to-cart area in an open shadow root, which a
     head-level stylesheet cannot reach — so we must walk into it. */
  function deepQueryAll(selector, root, out) {
    root = root || document;
    out = out || [];
    try {
      root.querySelectorAll(selector).forEach(function (el) { out.push(el); });
    } catch (e) {}
    root.querySelectorAll('*').forEach(function (el) {
      if (el.shadowRoot) deepQueryAll(selector, el.shadowRoot, out);
    });
    return out;
  }

  function setEspresso(el) {
    // idempotent: skip if already espresso-important (prevents observer loops)
    if (el.style && el.style.color === 'rgb(53, 51, 45)' &&
        el.style.getPropertyPriority('color') === 'important') return;
    if (el.style) el.style.setProperty('color', ESPRESSO, 'important');
  }

  /* #5 — Pre-order / ship-by line.
     The Dash (DC Order Limits) app injects <p class="dash-shipping-policy">
     inside the add-to-cart shadow root (beside the quantity), with inline white
     text. CRITICAL: we must NOT move or remove the app's node — doing so makes
     the app re-inject it, and the two scripts then fight, bumping the page
     layout every cycle (the flicker bug). Instead we HIDE the app's node in
     place (no move/remove -> no re-inject war) and render ONE stable line of
     our own, espresso, above the whole buy row. Our line is created once and
     only its text is updated, so it never causes layout jumps. */
  function fixPreorder() {
    var anchor = document.querySelector('.buy-buttons-block') ||
                 document.querySelector('product-form-component');
    if (!anchor || !anchor.parentNode) return;

    // The app's own note(s) — exclude our own copy.
    var notes = deepQueryAll('.dash-shipping-policy').filter(function (el) {
      return !el.classList.contains('kula-preorder-note');
    });
    var mine = document.querySelector('.kula-preorder-note');

    if (!notes.length) {            // not a pre-order product / not injected yet
      if (mine) mine.remove();
      return;
    }

    // Newest app note carries the current ship date text.
    var text = (notes[notes.length - 1].textContent || '').replace(/\s+/g, ' ').trim();

    // Hide the app's native note(s) IN PLACE — never move/remove (guarded so we
    // don't thrash the style attribute).
    notes.forEach(function (el) {
      if (el.style.display !== 'none') el.style.setProperty('display', 'none', 'important');
    });

    if (!text) { if (mine) mine.remove(); return; }

    // Our stable line: create once; update text/position only when it changed.
    if (!mine) {
      mine = document.createElement('p');
      mine.className = 'dash-shipping-policy kula-preorder-note';
    }
    if (mine.textContent !== text) mine.textContent = text;
    if (anchor.previousElementSibling !== mine) anchor.parentNode.insertBefore(mine, anchor);
    setEspresso(mine);
  }

  /* #1 — Mega-menu product hover preview.
     Server-side snippet (snippets/mega-menu-list.liquid, the
     menu_content_type == 'product_hover_preview' branch) renders a
     [data-hover-preview-container] holding .mega-menu__link--hover-preview
     links (each carrying data-preview-image + data-preview-title) and a
     [data-preview-area] with [data-preview-image-el], [data-preview-title-el]
     and [data-preview-link]. On link hover we swap the preview image (fade via
     .is-loading), title and click-through URL, and move the .is-active marker.
     Ported from the dev's live header-menu.js; the ONLY change is a per-container
     init guard so repeated run()/observer ticks don't stack duplicate listeners
     (the live copy lived in a once-run file and didn't need one). */
  function initHoverPreview() {
    deepQueryAll('[data-hover-preview-container]').forEach(function (container) {
      if (container.dataset.kulaHoverInit === '1') return;   // already wired
      var previewImage = container.querySelector('[data-preview-image-el]');
      var previewTitle = container.querySelector('[data-preview-title-el]');
      var previewLink  = container.querySelector('[data-preview-link]');
      if (!previewImage) return;
      container.dataset.kulaHoverInit = '1';

      container.querySelectorAll('.mega-menu__link--hover-preview[data-preview-image]')
        .forEach(function (link) {
          link.addEventListener('mouseenter', function () {
            var newImageSrc = link.getAttribute('data-preview-image');
            var newTitle = link.getAttribute('data-preview-title');
            var newUrl = link.getAttribute('href');

            container.querySelectorAll('.mega-menu__link--hover-preview')
              .forEach(function (l) { l.classList.remove('is-active'); });
            link.classList.add('is-active');

            if (previewLink && newUrl) previewLink.setAttribute('href', newUrl);

            if (newImageSrc && previewImage.getAttribute('src') !== newImageSrc) {
              previewImage.classList.add('is-loading');
              var img = new Image();
              img.onload = function () {
                previewImage.src = newImageSrc;
                previewImage.alt = newTitle || '';
                previewImage.classList.remove('is-loading');
              };
              img.onerror = function () { previewImage.classList.remove('is-loading'); };
              img.src = newImageSrc;
            }

            if (previewTitle) previewTitle.textContent = newTitle || '';
          });
        });
    });
  }

  /* #3 — Gallery color filter is handled SERVER-SIDE in the gallery snippet
     (snippets/product-media-gallery-content.liquid, gated on the block's
     filter_by_alt setting) so the carousel, dots and counter all stay clean.
     No client-side gallery filtering here. */

  /* #7 — Pre-order button label. The Dash (DC Order Limits) app embed injects
     its hidden line-item properties into the cart form fine on Horizon 4.x, but
     its "Pre-order" button relabel no longer lands: Horizon's add-to-cart button
     keeps its label as a bare text node next to icon <span>s and the theme's
     add-to-cart component re-renders it on variant change. So we own the label:
     whenever a /cart/add form carries Dash's properties[_preorder] input, the
     submit button reads "Pre-order"; when the property is absent (in-stock
     variant), the original label is restored. No-op when already correct, so
     the MutationObserver re-runs don't loop. */
  var PREORDER_LABEL = 'Pre-order';
  function buttonTextNode(btn) {
    for (var i = 0; i < btn.childNodes.length; i++) {
      var n = btn.childNodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) return n;
    }
    return null;
  }
  function relabelPreorderButtons() {
    deepQueryAll('form[action*="/cart/add"]').forEach(function (form) {
      var btn = form.querySelector('button[type="submit"][name="add"], button.add-to-cart-button');
      if (!btn) return;
      var tn = buttonTextNode(btn);
      if (!tn) return;
      var isPreorder = !!form.querySelector('input[name="properties[_preorder]"]');
      var current = tn.textContent.trim();
      if (isPreorder) {
        if (current !== PREORDER_LABEL) {
          if (!btn.dataset.kulaOrigLabel) btn.dataset.kulaOrigLabel = current;
          tn.textContent = ' ' + PREORDER_LABEL + ' ';
        }
      } else if (btn.dataset.kulaOrigLabel && current === PREORDER_LABEL) {
        tn.textContent = ' ' + btn.dataset.kulaOrigLabel + ' ';
      }
    });
  }

  /* #8 — Hydrant Wedge add-on (blocks/kula-wedge-addon.liquid).
     The block renders one checkbox pill ("Add Wedge") under the stem-length
     picker, unchecked by default, plus two maps: wedge Color -> wedge variant
     id, and hydrant variant id -> Color. On add-to-cart with the pill checked
     we intercept the form submit in the CAPTURE phase (before Horizon's
     product-form-component handles it), add the finish-matched wedge via
     /cart/add.js, then re-submit the form with a bypass flag so Horizon's own
     add-to-cart runs untouched (cart drawer, fly-to-cart, events). If the wedge
     add fails (e.g. product not published to the Online Store), we still add
     the hydrant and show the block's error line. Checked state survives the
     section morph on variant change via sessionStorage. */
  function wedgeStorageKey(box) { return 'kula-wedge:' + (box.dataset.productId || 'p'); }
  function wedgeSelectedColor(box, form) {
    var picker = document.querySelector('variant-picker input[data-option-name="Color"]:checked') ||
                 document.querySelector('variant-picker input[type="radio"][data-option-name="Color"][data-current-checked="true"]');
    if (picker && picker.value) return picker.value;
    var sel = document.querySelector('variant-picker select[name="options[Color]"]');
    if (sel && sel.value) return sel.value;
    try {
      var hmap = JSON.parse(box.dataset.hydrantMap || '{}');
      var idInput = form && form.querySelector('input[name="id"]');
      if (idInput && hmap[idInput.value]) return hmap[idInput.value];
    } catch (e) {}
    return null;
  }
  function wedgeVariantId(box, form) {
    var color = wedgeSelectedColor(box, form);
    try {
      var wmap = JSON.parse(box.dataset.wedgeMap || '{}');
      if (color && wmap[color]) return String(wmap[color]);
      var keys = Object.keys(wmap);
      if (keys.length === 1) return String(wmap[keys[0]]);
    } catch (e) {}
    return null;
  }
  function wedgeShowError(box, msg) {
    var el = box.querySelector('[data-kula-wedge-error]');
    if (!el) return;
    el.textContent = msg || box.dataset.errorText || 'The wedge could not be added.';
    el.hidden = false;
    clearTimeout(el._kulaT);
    el._kulaT = setTimeout(function () { el.hidden = true; }, 10000);
  }
  function wedgeAdd(box, form) {
    var vid = wedgeVariantId(box, form);
    if (!vid) return Promise.reject(new Error('No wedge variant for selected finish'));
    var item = { id: Number(vid) || vid, quantity: 1 };
    if (box.dataset.copyProperties === 'true' && form) {
      var props = {};
      form.querySelectorAll('input[name^="properties["]').forEach(function (inp) {
        var m = inp.name.match(/^properties\[(.+)\]$/);
        if (m && inp.value !== '') props[m[1]] = inp.value;
      });
      if (Object.keys(props).length) item.properties = props;
    }
    var url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    if (!/\.js$/.test(url)) url = url.replace(/\/?$/, '') .replace(/\/cart\/add$/, '/cart/add.js');
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (!res.ok || (res.body && res.body.status)) {
          throw new Error((res.body && (res.body.description || res.body.message)) || 'Cart error');
        }
        return res.body;
      });
  }
  function initWedgeAddon() {
    deepQueryAll('[data-kula-wedge]').forEach(function (box) {
      var input = box.querySelector('[data-kula-wedge-input]');
      if (!input) return;
      // Restore checked state after a section morph (variant change re-render).
      try {
        if (input.dataset.kulaRestored !== '1') {
          input.dataset.kulaRestored = '1';
          if (sessionStorage.getItem(wedgeStorageKey(box)) === '1') input.checked = true;
        }
      } catch (e) {}
      if (box.dataset.kulaWedgeInit === '1') return;
      box.dataset.kulaWedgeInit = '1';
      input.addEventListener('change', function () {
        try { sessionStorage.setItem(wedgeStorageKey(box), input.checked ? '1' : '0'); } catch (e) {}
      });
    });
  }
  // One document-level capture listener: runs before product-form-component's handler.
  var wedgeSubmitBound = false;
  function bindWedgeSubmit() {
    if (wedgeSubmitBound) return;
    wedgeSubmitBound = true;
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (form.dataset.kulaWedgeBypass === '1') { delete form.dataset.kulaWedgeBypass; return; }
      var box = document.querySelector('[data-kula-wedge]');
      if (!box) return;
      var input = box.querySelector('[data-kula-wedge-input]');
      if (!input || !input.checked) return;
      var idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;                          // not the product form
      e.preventDefault();
      e.stopImmediatePropagation();
      var btn = form.querySelector('button[type="submit"][name="add"], button.add-to-cart-button');
      if (btn) btn.setAttribute('aria-busy', 'true');
      function proceed() {
        if (btn) btn.removeAttribute('aria-busy');
        form.dataset.kulaWedgeBypass = '1';
        if (form.requestSubmit) form.requestSubmit(btn || undefined);
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
      wedgeAdd(box, form).then(function () {
        try { sessionStorage.setItem(wedgeStorageKey(box), '0'); } catch (err) {}
        input.checked = false;                        // one-shot: don't double-add on a second click
        proceed();
      }).catch(function (err) {
        console.warn('[kula-wedge] wedge add failed:', err && err.message);
        wedgeShowError(box);
        proceed();
      });
    }, true);
  }

  function run() { fixPreorder(); initHoverPreview(); relabelPreorderButtons(); initWedgeAddon(); }

  function init() {
    bindWedgeSubmit();
    run();
    // The app injects the note asynchronously; the document observer can't see
    // inside shadow roots, so also poll briefly after load.
    [150, 400, 800, 1500, 2500].forEach(function (d) { setTimeout(run, d); });

    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(run, 120);
    }).observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest &&
          e.target.closest('variant-picker, .variant-option, .gm_swatch_list')) {
        setTimeout(run, 80);
        setTimeout(run, 350);
        setTimeout(run, 700);
      }
    }, true);

    // Re-wire after Section Rendering API / theme-editor section reloads
    // (the mega-menu re-renders content on its second pass).
    document.addEventListener('shopify:section:load', function () { setTimeout(run, 0); }, false);

    /* #6b — Single-open accordions. Opening one row closes the other rows in
       the SAME .accordion (e.g. Product Features + Specifications can't both be
       open). This matches the dev's live theme; stock 4.1.1 allows multiple
       open. The native <details> 'toggle' event does NOT bubble, so we listen
       in the capture phase. The `closest('.accordion') === accordion` guard
       keeps nested accordions (the FAQ sub-accordions) independent — only rows
       at the same level close each other. Setting open=false fires a toggle
       with open=false, which we ignore, so there's no cascade/loop. */
    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d || d.tagName !== 'DETAILS' || !d.open) return;
      var accordion = d.closest && d.closest('.accordion');
      if (!accordion) return;
      accordion.querySelectorAll('details[open]').forEach(function (other) {
        if (other !== d && other.closest('.accordion') === accordion) other.open = false;
      });
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
